import React from 'react';
import {
  FormLabel,
  FormControlLabel,
  Radio,
  TextField,
  CircularProgress,
  Typography,
  Grid
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import {
  FormPageContainer,
  FormHeader,
  FormSubHeader,
  StyledForm,
  SubmitButton,
  RadioGroupContainer,
  StyledRadioGroup,
  ButtonGroupContainer,
  SectionContainer,
  ChildrenListGrid,
  ChildItemPaper,
  AgeDisplayTypography,
  AddButton,
  RemoveButton,
  TrustSummaryBox,
  InfoAlert,
  TrusteeDetailsBox,
  BackButton
} from './ChildrenTrustForm.styles';

export default function ChildrenTrustForm({
    formData,
    handleSubmit,
    handleBack,
    isSubmitting,
    setFormData,
    addChild,
    updateChild,
    removeChild,
}) {
    const handleRadioChange = (event) => {
        const { name, value } = event.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value === 'true'
        }));
    };

    const handleChildChange = (childId, field, value) => {
        updateChild(childId, field, value);
    };
    
    const handleChildDateChange = (childId, date) => {
        const dateString = date ? date.toISOString().split('T')[0] : '';
            updateChild(childId, 'dateOfBirth', dateString);
        if(dateString) {
        } else {
            updateChild(childId, 'age', '');
            updateChild(childId, 'isMinor', false);
        }
    };

    const handleTrusteeChange = (event) => {
        const { name, value } = event.target;
        setFormData(prevData => ({
            ...prevData,
            trustee: {
                ...prevData.trustee,
                [name]: value,
            }
        }));
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <FormPageContainer>
                <FormHeader variant="h4" component="h1">Will & Testament Creator</FormHeader>
                <FormSubHeader variant="h5" component="h2">Step 4: Children's Trust</FormSubHeader>
                <StyledForm component="form" onSubmit={handleSubmit} noValidate>
                    <RadioGroupContainer component="fieldset" variant="standard">
                        <FormLabel component="legend">Do you currently have any children?</FormLabel>
                        <StyledRadioGroup
                            aria-label="has-children"
                            name="hasChildren"
                            value={formData.hasChildren.toString()}
                            onChange={(e) => {
                                const hasChildrenBool = e.target.value === 'true';
                                setFormData(prev => ({
                                    ...prev,
                                    hasChildren: hasChildrenBool,
                                    hasMinorChildren: hasChildrenBool ? prev.hasMinorChildren : false,
                                    children: hasChildrenBool ? prev.children : [],
                                }));
                            }}
                        >
                            <FormControlLabel value="true" control={<Radio />} label="Yes" />
                            <FormControlLabel value="false" control={<Radio />} label="No" />
                        </StyledRadioGroup>
                    </RadioGroupContainer>

                    {formData.hasChildren && (
                        <>
                            <SectionContainer variant="outlined">
                                <Typography variant="h6" gutterBottom>Children's Details</Typography>

                                {formData.children.length === 0 && (
                                    <Typography variant="body2" color="textSecondary">
                                        No children added yet. Use the button below to add children.
                                    </Typography>
                                )}

                                {formData.children.length > 0 && (
                                    <ChildrenListGrid container spacing={2}>
                                        {formData.children.map((child) => (
                                            <Grid item xs={12} sm={6} md={4} key={child.id}>
                                                <ChildItemPaper variant="outlined">
                                                    <TextField
                                                        label="Full Name"
                                                        value={child.name}
                                                        onChange={(e) => handleChildChange(child.id, "name", e.target.value)}
                                                        required
                                                        fullWidth
                                                        size="small"
                                                    />
                                                    <DatePicker
                                                        label="Date of Birth"
                                                        value={child.dateOfBirth ? new Date(child.dateOfBirth) : null}
                                                        onChange={(date) => handleChildDateChange(child.id, date)}
                                                        slotProps={{ textField: { fullWidth: true, required: true, size: 'small' } }}
                                                    />

                                                    {child.age !== "" && child.age !== null && (
                                                        <AgeDisplayTypography variant="body2">
                                                            Age: {child.age} ({child.isMinor ? "Minor" : "Adult"})
                                                        </AgeDisplayTypography>
                                                    )}
                                                    <RemoveButton
                                                        variant="contained"
                                                        onClick={() => removeChild(child.id)}
                                                    >
                                                        Remove
                                                    </RemoveButton>
                                                </ChildItemPaper>
                                            </Grid>
                                        ))}
                                    </ChildrenListGrid>
                                )}

                                <AddButton
                                    variant="contained"
                                    onClick={addChild}
                                >
                                    Add Child
                                </AddButton>
                            </SectionContainer>

                            {formData.hasMinorChildren && (
                                <SectionContainer variant="outlined">
                                    <Typography variant="h6" gutterBottom>Children's Trust Information</Typography>
                                    <InfoAlert severity="info">
                                        Since you have children under the age of 18, a Children's Trust will be added to your will. This trust will protect and manage their inheritance until they reach the specified ages.
                                    </InfoAlert>

                                    <TrustSummaryBox>
                                        <Typography variant="subtitle1" component="h4" gutterBottom>Trust Details:</Typography>
                                        <ul>
                                            <li><Typography variant="body2">Children under 18: Assets held in trust until age 21</Typography></li>
                                            <li><Typography variant="body2">Children between 21-25: Half of assets released at 21, balance at 25</Typography></li>
                                            <li><Typography variant="body2">Children over 25: Full inheritance released immediately</Typography></li>
                                        </ul>
                                    </TrustSummaryBox>

                                    <RadioGroupContainer component="fieldset" variant="standard">
                                        <FormLabel component="legend">Who should act as Trustee(s)?</FormLabel>
                                        <StyledRadioGroup
                                            aria-label="executors-as-trustees"
                                            name="executorsAsTrustees"
                                            value={formData.executorsAsTrustees.toString()}
                                            onChange={handleRadioChange}
                                        >
                                            <FormControlLabel value="true" control={<Radio />} label="Same as Executors" />
                                            <FormControlLabel value="false" control={<Radio />} label="Different Trustee" />
                                        </StyledRadioGroup>
                                    </RadioGroupContainer>

                                    {!formData.executorsAsTrustees && (
                                        <TrusteeDetailsBox>
                                            <Typography variant="subtitle1" gutterBottom>Trustee Details</Typography>
                                            <TextField
                                                label="First Name"
                                                name="firstName"
                                                value={formData.trustee.firstName}
                                                onChange={handleTrusteeChange}
                                                required
                                                fullWidth
                                            />
                                            <TextField
                                                label="Last Name"
                                                name="lastName"
                                                value={formData.trustee.lastName}
                                                onChange={handleTrusteeChange}
                                                required
                                                fullWidth
                                            />
                                            <TextField
                                                label="ID Number"
                                                name="idNumber"
                                                value={formData.trustee.idNumber}
                                                onChange={handleTrusteeChange}
                                                required
                                                fullWidth
                                            />
                                        </TrusteeDetailsBox>
                                    )}
                                </SectionContainer>
                            )}
                        </>
                    )}

                    <ButtonGroupContainer>
                        <BackButton
                            type="button"
                            variant="contained"
                            onClick={handleBack}
                            disabled={isSubmitting}
                        >
                            Back
                        </BackButton>
                        <SubmitButton
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={isSubmitting}
                            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                            onClick={(e) => { handleSubmit(e) }}
                        >
                            {isSubmitting ? 'Processing...' : 'Next'}
                        </SubmitButton>
                    </ButtonGroupContainer>
                </StyledForm>
            </FormPageContainer>
        </LocalizationProvider>
    );
} 